import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { FiEdit2, FiShare2, FiGlobe, FiMail, FiAlertTriangle } from 'react-icons/fi';
import { RiVerifiedBadgeFill } from 'react-icons/ri';
import { SiSpotify, SiApplemusic, SiSoundcloud, SiInstagram, SiTiktok } from 'react-icons/si';
import { FaXTwitter } from 'react-icons/fa6';
import {
  useFollowUser,
  useUnfollowUser,
  useSubscribeToCreator,
  useUnsubscribeFromCreator,
} from '../../api/profile';
import { getAbsoluteProfileUrl } from '../../utils/profileUrl';
import { Avatar, CoverImage } from '../ui/Avatar';
import { useAuth } from '../../hooks/useAuth';
import { ModalBackdrop, ModalContentWrapper } from '../mail/Modals';
import { AnimatePresence } from 'framer-motion';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProfileData {
  id: string | number;
  full_name?: string;
  username?: string;
  bio?: string;
  prof_pics?: string | null;
  cover_photo?: string | null;
  follower_count?: number;
  following_count?: number;
  views?: number;
  is_verified?: boolean;
  // Social / music
  spotify?: string | null;
  apple_music?: string | null;
  soundcloud?: string | null;
  instagram?: string | null;
  twitter?: string | null;
  tiktok?: string | null;
  website?: string | null;
  is_following?: boolean;
  account_status?: 'active' | 'deactivated' | 'suspended' | 'deleted';
  subscriptions_enabled?: boolean;
  email_features_eligible?: boolean;
  subscriber_count?: number;
  is_subscribed?: boolean;
}

interface ProfileHeaderProps {
  profile: ProfileData;
  isOwner?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCount(n?: number): string {
  if (!n && n !== 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function normalizeExternalUrl(url?: string | null): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const isValidSocialUrl = (url: string) => {
  if (!url) return true;
  try {
    // Basic URL regex that ensures at least a domain and TLD, or http(s) protocol
    const pattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
    return pattern.test(url);
  } catch {
    return false;
  }
};

// ─── Component ───────────────────────────────────────────────────────────────

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ profile, isOwner = false }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const search: Record<string, unknown> = useSearch({ strict: false });
  const hasAutoTriggeredSubscribe = useRef(false);

  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Modals state
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);

  useEffect(() => {
    if (
      isAuthenticated &&
      (search?.subscribe === '1' || search?.subscribe === 'true') &&
      !profile.is_subscribed &&
      !hasAutoTriggeredSubscribe.current
    ) {
      hasAutoTriggeredSubscribe.current = true;
      setShowSubscribeModal(true);
    }
  }, [isAuthenticated, search, profile.is_subscribed]);

  const musicPlatforms = [
    { key: 'spotify', label: 'Spotify', url: profile.spotify, Icon: SiSpotify, color: '#1DB954' },
    {
      key: 'apple_music',
      label: 'Apple Music',
      url: profile.apple_music,
      Icon: SiApplemusic,
      color: '#fc3c44',
    },
    {
      key: 'soundcloud',
      label: 'SoundCloud',
      url: profile.soundcloud,
      Icon: SiSoundcloud,
      color: '#FF5500',
    },
  ].filter((p) => p.url);

  const socials = [
    {
      key: 'instagram',
      label: 'Instagram',
      url: profile.instagram,
      Icon: SiInstagram,
      color: '#E1306C',
    },
    { key: 'twitter', label: 'Twitter (X)', url: profile.twitter, Icon: FaXTwitter, color: '#fff' },
    { key: 'tiktok', label: 'TikTok', url: profile.tiktok, Icon: SiTiktok, color: '#69C9D0' },
  ].filter((p) => p.url);

  const { mutate: follow, isPending: followPending } = useFollowUser();
  const { mutate: unfollow, isPending: unfollowPending } = useUnfollowUser();
  const { mutate: subscribe, isPending: subscribePending } = useSubscribeToCreator();
  const { mutate: unsubscribe, isPending: unsubscribePending } = useUnsubscribeFromCreator();

  const shareUrl = getAbsoluteProfileUrl({ id: profile.id, username: profile.username });
  const shareText = `Check out ${profile.username ? `@${profile.username}` : 'this profile'} on Musicade`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);
  const socialShareLinks = [
    {
      key: 'x',
      label: 'Share on X',
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      key: 'whatsapp',
      label: 'Share on WhatsApp',
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
    },
    {
      key: 'linkedin',
      label: 'Share on LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
  ];

  const handleFollowToggle = () => {
    if (profile.is_following) {
      console.log(`[ProfileHeader] Triggering UNFOLLOW for ${profile.id}`);
      unfollow(
        { userId: profile.id, username: profile.username },
        {
          onSuccess: () => {
            // Toast handled by mutation
          },
          onError: () => {
            // Toast handled by mutation
          },
        },
      );
    } else {
      console.log(`[ProfileHeader] Triggering FOLLOW for ${profile.id}`);
      follow(
        { userId: profile.id, username: profile.username },
        {
          onSuccess: () => {
            // Toast handled by mutation
          },
          onError: () => {
            // Toast handled by mutation
          },
        },
      );
    }
  };

  const handleCopyProfileLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      setIsCopied(false);
    }
  };

  const handleSocialClick = (
    e: React.MouseEvent,
    url: string | null | undefined,
    label: string,
  ) => {
    if (!url) {
      e.preventDefault();
      return;
    }

    const normalizedUrl = normalizeExternalUrl(url);
    if (!isValidSocialUrl(normalizedUrl)) {
      e.preventDefault();
      alert(`We couldn't open this external link: "${url}". Please ensure it's a valid URL.`);
      console.warn(`[ProfileHeader] Invalid social URL for ${label}:`, url);
      return;
    }
  };

  return (
    <div className="relative w-full">
      {/* ── Cover Image ─────────────────────────────────────────────────── */}
      <div className="relative w-full aspect-[3/1] overflow-hidden bg-zinc-900">
        <CoverImage src={profile.cover_photo} alt="Cover" />
        {/* Fade to black at bottom */}
        <div className="absolute inset-0 bg-linear-to-b from-transparent via-transparent to-black/80" />
      </div>

      {/* ── Main Profile Area ────────────────────────────────────────────── */}
      <div className="relative bg-black px-4 md:px-8">
        {/* ── Avatar (overlaps cover) ────────────────────────────────── */}
        <div className="absolute -top-16 left-4 md:left-8">
          <Avatar
            src={profile.prof_pics}
            alt={profile.full_name || profile.username || 'Profile'}
            name={profile.full_name || profile.username}
            size="4xl"
            className="border-[3px] border-black shadow-xl"
            iconSize={52}
          />
        </div>

        {/* ── Right side: Music Platforms + Socials + Website (Desktop Only) ─────── */}
        <div className="hidden md:flex absolute top-4 right-8 flex-col gap-4 items-end max-w-[min(360px,28vw)] w-full">
          {musicPlatforms.length > 0 && (
            <div>
              <p className="text-zinc-500 text-[11px] font-medium uppercase tracking-widest mb-1.5 text-right">
                Music Platforms
              </p>
              <div className="flex flex-wrap gap-2 justify-end max-w-full">
                {musicPlatforms.map(({ key, label, url, Icon, color }) => (
                  <a
                    key={key}
                    href={normalizeExternalUrl(url)}
                    onClick={(e) => handleSocialClick(e, url, label)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900 border border-white/10 text-white text-[12px] font-medium hover:border-white/25 transition-all max-w-full"
                  >
                    <Icon size={13} style={{ color }} />
                    <span className="truncate">{label}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {socials.length > 0 && (
            <div>
              <p className="text-zinc-500 text-[11px] font-medium uppercase tracking-widest mb-1.5 text-right">
                Socials
              </p>
              <div className="flex flex-wrap gap-2 justify-end max-w-full">
                {socials.map(({ key, label, url, Icon, color }) => (
                  <a
                    key={key}
                    href={normalizeExternalUrl(url)}
                    onClick={(e) => handleSocialClick(e, url, label)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900 border border-white/10 text-white text-[12px] font-medium hover:border-white/25 transition-all max-w-full"
                  >
                    <Icon size={13} style={{ color }} />
                    <span className="truncate">{label}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {profile.website && (
            <div>
              <p className="text-zinc-500 text-[11px] font-medium uppercase tracking-widest mb-1.5 text-right">
                Website
              </p>
              <a
                href={normalizeExternalUrl(profile.website)}
                onClick={(e) => handleSocialClick(e, profile.website, 'Website')}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900 border border-white/10 text-white text-[12px] font-medium hover:border-white/25 transition-all w-full max-w-[320px]"
              >
                <FiGlobe size={13} className="text-blue-400" />
                <span className="max-w-[160px] truncate">
                  {profile.website.replace(/^https?:\/\//, '')}
                </span>
              </a>
            </div>
          )}
        </div>

        {/* ── Username + actions + stats ─────────────────────────────────── */}
        <div className="pt-[80px] pb-6 w-full">
          {/* Name + verification */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-bold text-[18px] md:text-[20px] leading-tight">
              {profile.username || profile.full_name}
            </span>
            {profile.is_verified && (
              <RiVerifiedBadgeFill size={18} className="text-[#3B82F6] shrink-0" />
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {isOwner ? (
              <>
                <Link
                  to="/profile/edit"
                  className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-2 md:py-1.5 rounded-full bg-zinc-800 border border-white/10 text-white text-[13px] font-medium hover:bg-zinc-700 transition-all shadow-lg active:scale-[0.98]"
                >
                  <FiEdit2 size={13} />
                  Edit Profile
                </Link>
                <button
                  onClick={() => setShowShareMenu((v) => !v)}
                  className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-2 md:py-1.5 rounded-full bg-zinc-800 border border-white/10 text-white text-[13px] font-medium hover:bg-zinc-700 transition-all shadow-lg active:scale-[0.98]"
                >
                  <FiShare2 size={13} />
                  Share Public Profile
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleFollowToggle}
                  disabled={followPending || unfollowPending}
                  className={`flex items-center gap-1.5 px-6 py-1.5 rounded-full text-[13px] font-semibold transition-all shadow-lg ${
                    profile.is_following
                      ? 'bg-zinc-800 border border-white/10 text-white hover:bg-zinc-700'
                      : 'bg-white text-black hover:bg-zinc-200'
                  } disabled:opacity-50`}
                >
                  {followPending || unfollowPending
                    ? 'Processing...'
                    : profile.is_following
                      ? 'Unfollow'
                      : 'Follow'}
                </button>

                {profile.email_features_eligible && profile.subscriptions_enabled && (
                  <button
                    onClick={() => {
                      if (!isAuthenticated) {
                        const searchParams = new URLSearchParams(window.location.search);
                        searchParams.set('subscribe', '1');
                        const searchStr = searchParams.toString();
                        const redirectUrl = `${window.location.pathname}${searchStr ? `?${searchStr}` : ''}`;
                        navigate({
                          to: '/sign-in',
                          search: { redirect: redirectUrl },
                        });
                        return;
                      }

                      if (profile.is_subscribed) {
                        setShowUnsubscribeModal(true);
                      } else {
                        setShowSubscribeModal(true);
                      }
                    }}
                    disabled={subscribePending || unsubscribePending}
                    className={`flex items-center gap-1.5 px-6 py-1.5 rounded-full text-[13px] font-semibold transition-all shadow-lg ${
                      profile.is_subscribed
                        ? 'bg-zinc-800 border border-white/10 text-white hover:bg-zinc-700'
                        : 'bg-blue-600 text-white hover:bg-blue-500'
                    } disabled:opacity-50`}
                  >
                    {subscribePending || unsubscribePending ? (
                      'Processing...'
                    ) : profile.is_subscribed ? (
                      <>
                        <FiMail size={13} />
                        Subscribed
                      </>
                    ) : (
                      <>
                        <FiMail size={13} />
                        Subscribe
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>

          {isOwner && showShareMenu && (
            <div className="mt-3 p-3 rounded-2xl border border-white/10 bg-zinc-900/70 backdrop-blur-sm max-w-md">
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  className="flex-1 bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-zinc-300"
                />
                <button
                  onClick={handleCopyProfileLink}
                  className="px-3 py-2 rounded-xl bg-white text-black text-[12px] font-semibold hover:bg-zinc-200 transition-colors"
                >
                  {isCopied ? 'Copied!' : 'Copy link'}
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {socialShareLinks.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded-full border border-white/15 text-[12px] text-zinc-200 hover:bg-white/5 transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-[13px]">
            <span>
              <span className="text-white font-bold">{formatCount(profile.follower_count)}</span>
              <span className="text-zinc-400 ml-1">Followers</span>
            </span>
            <span className="text-zinc-600">•</span>
            <span>
              <span className="text-white font-bold">{formatCount(profile.following_count)}</span>
              <span className="text-zinc-400 ml-1">Following</span>
            </span>
            {profile.views !== undefined && profile.views !== null && (
              <>
                <span className="text-zinc-600">•</span>
                <span>
                  <span className="text-white font-bold">{formatCount(profile.views)}</span>
                  <span className="text-zinc-400 ml-1">Viewers</span>
                </span>
              </>
            )}
            {isOwner && profile.email_features_eligible && (
              <>
                <span className="text-zinc-600">•</span>
                <span>
                  <span className="text-white font-bold">
                    {formatCount(profile.subscriber_count)}
                  </span>
                  <span className="text-zinc-400 ml-1">Subscribers</span>
                </span>
              </>
            )}
          </div>

          {/* Bio */}
          {profile.bio && (
            <p className="mt-4 text-zinc-400 text-[13px] leading-relaxed max-w-[600px]">
              {profile.bio}
            </p>
          )}

          {/* ── Mobile-Only: Music Platforms + Socials + Website ────────── */}
          <div className="flex md:hidden flex-col gap-6 mt-8 pb-4">
            {musicPlatforms.length > 0 && (
              <div className="space-y-3">
                <p className="text-zinc-500 text-[11px] font-bold uppercase tracking-[0.2em]">
                  Music Platforms
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {musicPlatforms.map(({ key, label, url, Icon, color }) => (
                    <a
                      key={key}
                      href={normalizeExternalUrl(url)}
                      onClick={(e) => handleSocialClick(e, url, label)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-zinc-900 border border-white/5 text-white text-[12px] font-medium active:bg-zinc-800 transition-all min-w-0"
                    >
                      <Icon size={14} style={{ color }} />
                      <span className="truncate">{label}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {(socials.length > 0 || profile.website) && (
              <div className="space-y-3">
                <p className="text-zinc-500 text-[11px] font-bold uppercase tracking-[0.2em]">
                  Socials & Web
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {socials.map(({ key, label, url, Icon, color }) => (
                    <a
                      key={key}
                      href={normalizeExternalUrl(url)}
                      onClick={(e) => handleSocialClick(e, url, label)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-zinc-900 border border-white/5 text-white text-[12px] font-medium active:bg-zinc-800 transition-all"
                    >
                      <Icon size={14} style={{ color }} />
                      <span className="capitalize">{key}</span>
                    </a>
                  ))}
                  {profile.website && (
                    <a
                      href={normalizeExternalUrl(profile.website)}
                      onClick={(e) => handleSocialClick(e, profile.website, 'Website')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sm:col-span-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-zinc-900 border border-white/5 text-white text-[12px] font-medium active:bg-zinc-800 transition-all min-w-0"
                    >
                      <FiGlobe size={14} className="text-blue-400" />
                      <span className="truncate max-w-[120px]">
                        {profile.website.replace(/^https?:\/\/(www\.)?/, '')}
                      </span>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showSubscribeModal && (
          <ModalBackdrop onClose={() => setShowSubscribeModal(false)}>
            <ModalContentWrapper
              onClose={() => setShowSubscribeModal(false)}
              maxWidthClass="max-w-[400px]"
            >
              <div className="p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4">
                  <FiMail size={20} className="text-blue-400" />
                </div>
                <h3 className="text-base font-bold text-white mb-2">Subscribe to mailing list</h3>
                <p className="text-xs text-zinc-400 leading-relaxed mb-6 max-w-[280px]">
                  Subscribe to {profile.username || 'this creator'}'s profile mailing list to
                  receive direct email updates, newsletters, and announcements.
                </p>
                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => setShowSubscribeModal(false)}
                    className="flex-1 h-10 rounded-full bg-zinc-900 border border-white/10 text-white text-[13px] font-semibold hover:bg-zinc-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowSubscribeModal(false);
                      subscribe({ userId: profile.id, username: profile.username });
                    }}
                    className="flex-1 h-10 rounded-full bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-500 transition-colors"
                  >
                    Subscribe
                  </button>
                </div>
              </div>
            </ModalContentWrapper>
          </ModalBackdrop>
        )}

        {showUnsubscribeModal && (
          <ModalBackdrop onClose={() => setShowUnsubscribeModal(false)}>
            <ModalContentWrapper
              onClose={() => setShowUnsubscribeModal(false)}
              maxWidthClass="max-w-[400px]"
            >
              <div className="p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
                  <FiAlertTriangle size={20} className="text-red-400" />
                </div>
                <h3 className="text-base font-bold text-white mb-2">Unsubscribe</h3>
                <p className="text-xs text-zinc-400 leading-relaxed mb-6 max-w-[280px]">
                  Are you sure you want to unsubscribe from {profile.username || 'this creator'}'s
                  profile mailing list? You will stop receiving their updates.
                </p>
                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => setShowUnsubscribeModal(false)}
                    className="flex-1 h-10 rounded-full bg-zinc-900 border border-white/10 text-white text-[13px] font-semibold hover:bg-zinc-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowUnsubscribeModal(false);
                      unsubscribe({ userId: profile.id, username: profile.username });
                    }}
                    className="flex-1 h-10 rounded-full bg-red-600 text-white text-[13px] font-semibold hover:bg-red-500 transition-colors"
                  >
                    Unsubscribe
                  </button>
                </div>
              </div>
            </ModalContentWrapper>
          </ModalBackdrop>
        )}
      </AnimatePresence>
    </div>
  );
};
